import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, SYNC_STATUS, recordHistoryBatch, addToSyncQueue, getSetting } from '../db/shiksha-db';
import { ArrowLeft, UploadCloud, ShieldAlert, CloudUpload } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAuth } from '../hooks/useAuth';
import { MARKING_SCHEMES } from '../config/marking-schemes';

const CO_SCHOLASTIC_AREAS = [
  'Discipline',
  'Art Education',
  'Health & Physical Education',
  'Work Education'
];

export function CoScholasticPage() {
  const { user } = useAuth();
  const { classId } = useParams();
  
  const [batchId] = useState(() => crypto.randomUUID());
  const [historyRecorded, setHistoryRecorded] = useState(false);

  const teacherProfile = useLiveQuery(
    () => (user?.email ? db.teachers.where('email').equals(user.email).first() : null),
    [user?.email]
  );

  const [term, setTerm] = useState('Term 1');
  const [sectionFilter, setSectionFilter] = useState('All');
  
  // Need to get academic year from settings for Co-Scholastic
  const academicYear = useLiveQuery(async () => await getSetting('academicYear')) || '2026-27';

  // Extract base class (e.g. Class IX from Class IX-A)
  const baseClass = (classId || '').split('-')[0].trim();

  const students = useLiveQuery(async () => {
    const all = await db.students.orderBy('rollNo').toArray();
    
    return all.filter(s => {
      const upper = (s.class?.toString() || '').toUpperCase();
      let isClass = true;
      const classKey = baseClass.toUpperCase();
      
      if (classKey === 'CLASS VI') isClass = ['6','VI','CLASS VI'].includes(upper);
      else if (classKey === 'CLASS VII') isClass = ['7','VII','CLASS VII'].includes(upper);
      else if (classKey === 'CLASS VIII') isClass = ['8','VIII','CLASS VIII'].includes(upper);
      else if (classKey === 'CLASS IX') isClass = ['9','IX','CLASS IX'].includes(upper);
      else if (classKey === 'CLASS X') isClass = ['10','X','CLASS X'].includes(upper);
      
      if (!isClass) return false;
      
      const sSection = (s.section || '').trim().toUpperCase();
      if (sectionFilter !== 'All') {
        return sSection === sectionFilter.toUpperCase();
      }
      return true;
    });
  }, [baseClass, sectionFilter]);

  const coScholastic = useLiveQuery(
    () => db.coScholastic.where({ term, academicYear }).toArray(),
    [term, academicYear]
  );

  const { triggerSync } = useOfflineSync();

  const handleBlur = async (studentId, area, grade) => {
    const validGrades = ['A', 'B', 'C', 'D', 'E', ''];
    const g = grade.trim().toUpperCase();
    
    if (!validGrades.includes(g)) {
      alert("Grade must be A, B, C, D, E, or blank.");
      return;
    }

    try {
      const existing = await db.coScholastic
        .where({ studentId, term, academicYear })
        .filter(m => m.area === area)
        .first();

      let recordId;
      if (existing) {
        await db.coScholastic.update(existing.id, {
          grade: g,
          syncStatus: SYNC_STATUS.QUEUED,
        });
        recordId = existing.id;
      } else {
        if (!g) return; // don't create empty records
        recordId = await db.coScholastic.add({
          studentId,
          area,
          grade: g,
          term,
          academicYear,
          syncStatus: SYNC_STATUS.QUEUED,
          batchId,
        });
      }

      await addToSyncQueue('coScholastic', recordId, existing ? 'update' : 'create', {
        studentId,
        area,
        term,
        grade: g,
        academicYear
      }, batchId);

      if (!historyRecorded) {
        recordHistoryBatch(batchId, `Saved Co-Scholastic Grades for ${baseClass}`);
        setHistoryRecorded(true);
      }
    } catch (error) {
      console.error('[CoScholasticPage] Save failed:', error);
    }
  };

  const getExistingGrade = (studentId, area) => {
    const m = coScholastic?.find(
      (m) => m.studentId === studentId && m.area === area
    );
    return m?.grade ?? '';
  };

  const isPendingSync = (studentId) => {
    return coScholastic?.some(
      (m) => m.studentId === studentId && m.syncStatus === SYNC_STATUS.QUEUED
    );
  };

  const handleSubmitBatch = async () => {
    if (!navigator.onLine) {
      alert("You are offline. Sync will happen automatically when connected.");
      return;
    }
    await triggerSync();
  };

  if (user?.role !== 'admin' && teacherProfile?.role !== 'admin') {
    if (teacherProfile?.classTeacherOf !== classId && teacherProfile?.classTeacherOf !== baseClass) {
      return (
        <div className="min-h-screen bg-paper flex items-center justify-center p-4">
          <div className="bg-white border border-red-100 rounded-xl p-8 max-w-md w-full text-center shadow-sm">
            <ShieldAlert size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-ink mb-2">Access Denied</h2>
            <p className="text-graphite mb-6">
              Only the Class Teacher can enter Co-Scholastic grades for {baseClass}.
            </p>
            <Link to="/teacher/dashboard" className="text-indigo-600 font-medium hover:underline">
              Return to Dashboard
            </Link>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher/dashboard"
              className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-ink" />
            </Link>
            <div>
              <div className="text-xs text-graphite mb-1 font-medium font-secondary">
                Dashboard &gt; {baseClass} &gt; Co-Scholastic
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-ink">
                Co-Scholastic Grades
              </h1>
            </div>
          </div>
          <button
            onClick={handleSubmitBatch}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-semibold transition-colors shadow-sm"
          >
            <UploadCloud size={16} />
            Submit Batch
          </button>
        </div>

        {/* Selectors */}
        <div className="bg-white border border-divider rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Term</label>
            <select
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
            >
              <option value="Term 1">Term 1</option>
              <option value="Term 2">Term 2</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Section</label>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
            >
              <option value="All">All Sections</option>
              <option value="A">Section A</option>
              <option value="B">Section B</option>
              <option value="C">Section C</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden overflow-x-auto">
          <div className="min-w-max">
            <div className="flex items-center border-b border-divider bg-gray-50 px-4 py-3">
              <div className="sticky left-0 bg-gray-50 z-20 w-56 flex-shrink-0">
                <span className="text-sm font-bold text-ink font-secondary">Student Name</span>
              </div>
              {CO_SCHOLASTIC_AREAS.map((area) => (
                <div key={area} className="w-36 flex-shrink-0 text-center px-2">
                  <span className="text-xs font-bold text-graphite uppercase tracking-wider block truncate" title={area}>
                    {area}
                  </span>
                </div>
              ))}
            </div>

            {students?.map((student) => (
              <div
                key={student.id}
                className="flex items-center border-b border-divider px-4 py-2 hover:bg-indigo-50/30 transition-colors"
              >
                <div className="sticky left-0 bg-white z-10 w-56 flex-shrink-0 pr-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-700">{student.rollNo}</span>
                  </div>
                  <div className="truncate flex items-center gap-1">
                    <p className="text-sm font-bold text-ink truncate" title={student.name}>{student.name}</p>
                    {isPendingSync(student.id) && <CloudUpload size={14} className="text-amber-500 flex-shrink-0" title="Pending Sync" />}
                  </div>
                </div>
                
                {CO_SCHOLASTIC_AREAS.map((area) => {
                  const existingVal = getExistingGrade(student.id, area);
                  return (
                    <div key={area} className="w-36 flex-shrink-0 flex justify-center px-2">
                      <input
                        type="text"
                        maxLength="1"
                        defaultValue={existingVal}
                        className="w-full min-h-[48px] border border-gray-300 rounded-md p-2 text-center text-base font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-colors duration-300 uppercase"
                        onBlur={(e) => handleBlur(student.id, area, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
